import { filesApi } from '../../../api/files.api';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  startUpload,
  updateUploadProgress,
  incrementUploadedFiles,
  finishUpload,
} from '../../../store/slices/loadingSlice';
import axios from 'axios';
import { downloadMultipleFiles } from '../utils/fileDownloader';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

interface UseFileUploadReturn {
  uploadFiles: (files: File[]) => Promise<void>;
  uploadFilesFromUrls: (urls: string[]) => Promise<void>;
  isUploading: boolean;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const dispatch = useAppDispatch();
  const { isUploading } = useAppSelector((state) => state.loading);
  const queryClient = useQueryClient();

  const validateFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        `Le fichier ${file.name} est trop volumineux. Taille maximale: 100MB`
      );
      return false;
    }
    return true;
  };

  const uploadFiles = async (files: File[]): Promise<void> => {
    try {
      // Vérifier la taille de tous les fichiers avant de commencer l'upload
      const validFiles = files.filter(validateFileSize);
      if (validFiles.length === 0) return;

      dispatch(startUpload({ totalFiles: validFiles.length }));

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        try {
          dispatch(
            updateUploadProgress({
              progress: (i / validFiles.length) * 100,
              currentFile: file.name,
            })
          );

          await filesApi.uploadFile(file);
          toast.success(`Le fichier ${file.name} a été uploadé avec succès`);

          dispatch(incrementUploadedFiles());
        } catch (err) {
          if (axios.isAxiosError(err)) {
            if (err.code === 'ECONNABORTED') {
              toast.error(
                `L'upload de ${file.name} a expiré. Veuillez réessayer avec un fichier plus petit ou une meilleure connexion.`
              );
            } else {
              toast.error(
                `Erreur lors de l'upload de ${file.name}: ${err.message}`
              );
            }
          } else {
            const error =
              err instanceof Error
                ? err
                : new Error("Une erreur est survenue lors de l'upload");
            toast.error(
              `Erreur lors de l'upload de ${file.name}: ${error.message}`
            );
          }
        }
      }
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Une erreur est survenue lors de l'upload");
      toast.error(error.message);
    } finally {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      dispatch(finishUpload());
    }
  };

  const uploadFilesFromUrls = async (urls: string[]): Promise<void> => {
    try {
      if (urls.length === 0) return;

      dispatch(startUpload({ totalFiles: urls.length }));

      const downloadResults = await downloadMultipleFiles(urls);

      if (downloadResults.length === 0) {
        toast.error(
          "Aucun fichier n'a pu être téléchargé depuis les liens fournis"
        );
        return;
      }

      const files = downloadResults.map((result) => result.file);
      const validFiles = files.filter(validateFileSize);

      if (validFiles.length === 0) {
        toast.error('Aucun PDF téléchargé ne respecte les critères de taille');
        return;
      }

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];

        try {
          dispatch(
            updateUploadProgress({
              progress: (i / validFiles.length) * 100,
              currentFile: file.name,
            })
          );

          await filesApi.uploadFile(file);
          toast.success(
            `Le PDF ${file.name} a été téléchargé et uploadé avec succès`
          );

          dispatch(incrementUploadedFiles());
        } catch (err) {
          if (axios.isAxiosError(err)) {
            if (err.code === 'ECONNABORTED') {
              toast.error(
                `L'upload de ${file.name} a expiré. Veuillez réessayer avec un fichier plus petit ou une meilleure connexion.`
              );
            } else {
              toast.error(
                `Erreur lors de l'upload de ${file.name}: ${err.message}`
              );
            }
          } else {
            const error =
              err instanceof Error
                ? err
                : new Error("Une erreur est survenue lors de l'upload");
            toast.error(
              `Erreur lors de l'upload de ${file.name}: ${error.message}`
            );
          }
        }
      }
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Une erreur est survenue lors du téléchargement des PDF');
      toast.error(error.message);
    } finally {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      dispatch(finishUpload());
    }
  };

  return {
    uploadFiles,
    uploadFilesFromUrls,
    isUploading,
  };
};
